import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, Clock, Layers, ShieldCheck, Users } from 'lucide-react';
import { courses, Course, courseDetails } from '../data/courses';
import EnrollmentModal from '../components/enrollment/EnrollmentModal';
import ReviewsSection from '../components/course/ReviewsSection';
import LmsCourseDetail from '../components/course/LmsCourseDetail';
import { useAuth } from '../components/auth/AuthContext';
import { lmsHref } from '../lib/surface';
import { Panel, PanelHead, Stamp } from '../components/lms/ui';

const CourseDetail: React.FC = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const course: Course | null = useMemo(() => {
    const all = Object.values(courses).flat();
    return all.find((c) => c.id === id) || null;
  }, [id]);

  const details = id ? courseDetails[id] : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  if (!course) {
    return <LmsCourseDetail id={id || ''} />;
  }

  const tuition = course.price;

  return (
    <div>
      <Link to="/courses" className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-2 hover:text-ink">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Programme register
      </Link>

      <div className="mt-5 flex flex-col gap-8 lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        <div className="order-0 min-w-0 lg:col-start-1 lg:row-start-1">
          <div className="flex flex-wrap items-center gap-3">
            <Stamp tone="issued">{course.category}</Stamp>
            <span className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-ink-2">
              LSEM-{course.id.replace(/[^a-zA-Z0-9]/g, '').slice(0, 6).toUpperCase()}
            </span>
          </div>
          <h1 className="mt-4 max-w-[24ch] text-[clamp(2rem,3.6vw,3rem)] font-semibold leading-[1.06] tracking-[-0.03em] text-ink">
            {course.title}
          </h1>
          {details?.tagline && (
            <p className="mt-3 max-w-[58ch] text-[16px] leading-7 text-ink-2">{details.tagline}</p>
          )}
          <p className="mt-3 max-w-[68ch] text-[14px] leading-6 text-ink-2">{course.description}</p>

          <div className="mt-6 grid grid-cols-2 gap-px border border-rule bg-rule sm:grid-cols-4">
            {[
              { label: 'Duration', value: course.duration, icon: Clock },
              { label: 'Level', value: course.level, icon: Layers },
              { label: 'Format', value: details?.format || 'Hybrid', icon: Users },
              { label: 'Award', value: details?.certification || `LSEM ${course.category}`, icon: ShieldCheck },
            ].map((fact) => (
              <div key={fact.label} className="bg-white px-4 py-3">
                <div className="flex items-center gap-1.5">
                  <fact.icon className="h-3.5 w-3.5 text-ink-2" strokeWidth={1.5} />
                  <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">{fact.label}</span>
                </div>
                <p className="mt-1.5 text-[12.5px] font-medium leading-4 text-ink">{fact.value}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="order-2 min-w-0 space-y-6 lg:order-none lg:col-start-1 lg:row-start-2">
          <Panel>
            <PanelHead title="Programme overview" />
            <div className="px-5 py-5">
              <p className="max-w-[70ch] whitespace-pre-wrap text-[13.5px] leading-6 text-ink-2">
                {details?.overview || course.description}
              </p>
            </div>
          </Panel>

          {details?.modules && details.modules.length > 0 && (
            <Panel>
              <PanelHead
                title="Curriculum"
                action={<span className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-ink-2">{details.modules.length} modules</span>}
              />
              <div className="divide-y divide-rule">
                {details.modules.map((mod, idx) => (
                  <div key={mod.title} className="grid gap-3 px-5 py-4 sm:grid-cols-[36px_minmax(0,1fr)]">
                    <span className="font-mono text-[10px] font-semibold text-ink-2">{String(idx + 1).padStart(2, '0')}</span>
                    <div>
                      <h3 className="text-[14px] font-semibold leading-5 text-ink">{mod.title}</h3>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1.5">
                        {mod.topics.map((topic) => (
                          <span key={topic} className="inline-flex items-start gap-1.5 text-[12px] leading-5 text-ink-2">
                            <span className="mt-[7px] h-1 w-1 flex-none bg-pine" aria-hidden="true" />
                            {topic}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          {details?.outcomes && details.outcomes.length > 0 && (
            <Panel>
              <PanelHead title="What you will be able to do" />
              <ul className="divide-y divide-rule">
                {details.outcomes.map((outcome) => (
                  <li key={outcome} className="flex items-start gap-2.5 px-5 py-3.5">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-success" strokeWidth={1.5} />
                    <span className="text-[12.5px] leading-5 text-ink">{outcome}</span>
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          {details?.audience && details.audience.length > 0 && (
            <Panel>
              <PanelHead title="Who the programme is for" />
              <ul className="grid gap-px bg-rule sm:grid-cols-2">
                {details.audience.map((item) => (
                  <li key={item} className="flex items-start gap-2.5 bg-white px-5 py-4">
                    <span className="mt-[7px] h-1.5 w-1.5 flex-none bg-pine" aria-hidden="true" />
                    <span className="text-[12.5px] leading-5 text-ink">{item}</span>
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          <ReviewsSection courseId={course.id} />
        </div>

        <aside className="order-1 space-y-4 lg:order-none lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:sticky lg:top-24">
          <Panel>
            <div className="border-b border-rule px-5 py-4">
              <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-ink-2">Programme tuition</p>
              <p className="tnum mt-1 text-[30px] font-semibold leading-none tracking-[-0.03em] text-ink">{tuition}</p>
              <p className="mt-1.5 text-[11.5px] text-ink-2">One payment · lifetime classroom access</p>
            </div>
            <div className="space-y-3 px-5 py-4">
              <button type="button" onClick={() => setIsModalOpen(true)} className="lms-btn-stamp w-full">
                Enroll in this programme <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </button>
              <a href={lmsHref(`/learn/${course.id}`)} className="lms-btn-secondary w-full">
                {user ? 'Open classroom' : 'Sign in to your classroom'}
              </a>
              <p className="flex items-center justify-center gap-1.5 text-[10.5px] text-ink-2">
                <ShieldCheck className="h-3.5 w-3.5 text-success" strokeWidth={1.5} /> Secure payment through Paystack
              </p>
            </div>
            <div className="divide-y divide-rule border-t border-rule text-[12px]">
              {[
                ['Starts', details?.startDate || 'Rolling admissions'],
              ].map(([label, value]) => (
                <div key={label} className="flex items-start justify-between gap-3 px-5 py-2.5">
                  <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-ink-2">{label}</span>
                  <span className="text-right font-medium text-ink">{value}</span>
                </div>
              ))}
            </div>
          </Panel>

          <Panel>
            <PanelHead title="Talk to admissions" />
            <div className="px-5 py-4">
              <p className="text-[12.5px] leading-5 text-ink-2">
                Compare this programme with another pathway before you commit. The academic team can advise on fit and workload.
              </p>
              <Link to="/contact" className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-pine hover:underline">
                Contact the school <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} />
              </Link>
            </div>
          </Panel>
        </aside>
      </div>

      <EnrollmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        courseId={course.id}
        courseTitle={course.title}
        coursePrice={course.price}
        priceValue={details?.priceValue}
      />
    </div>
  );
};

export default CourseDetail;
